<template>
	<div>
		<ui-box :form="refs.redeemForm" :eager="!!product.id" new-forms>

			<form-title title="Redemptions"/>

			<form-divider inner/>

			<form-subsection title="How To Redeem">
				Where will your customers use this coupon and are there special instructions?
			</form-subsection>

			<v-row>
				<v-col cols="12" sm="5" md="4" lg="3">
					<v-select
							v-model="coupon.redemption_settings.mode"
							:append-icon="redemptionLocationIcon"
							class="rounded-lg required"
							clearable
							hide-details="auto"
							:items="redemptionLocations"
							label="Location"
							outlined
							required
							:rules="rules.redemption_location"
							@change="(value) => modeChanged(value, true)"
					></v-select>
				</v-col>
				<v-col
						v-if="['Phone', 'Online'].includes(coupon.redemption_settings.mode)"
						cols="12"
						sm="7"
						md="8"
						lg="9"
				>
					<v-row v-if="coupon.redemption_settings.mode === 'Online'">
						<v-col cols="12" md="6">
							<v-text-field
									v-model="coupon.redemption_settings.website"
									class="rounded-lg required"
									clearable
									label="URL/Link"
									outlined
									:rules="rules.website"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6" v-if="enablePromoTicketField">
							<v-text-field
									v-model="promoTicket"
									class="rounded-lg"
									clearable
									label="Promo Code"
									outlined
							></v-text-field>
						</v-col>
					</v-row>
					<v-row v-if="coupon.redemption_settings.mode === 'Phone'">
						<v-col>
							<vue-tel-input-vuetify
									ref="phone"
									v-model="coupon.redemption_settings.phone_number"
									append-icon="mdi-menu-down"
									class="required rounded-lg"
									clearable
									:defaultCountry="countryCode"
									label="Number"
									mode="international"
									outlined
									:preferred-countries="['us', 'ca']"
									:rules="phoneRule"
									@input="phoneInputEvent"
									@keydown="phoneKeyEvent"
							></vue-tel-input-vuetify>
						</v-col>
					</v-row>
				</v-col>
			</v-row>

			<v-row>
				<v-col>
					<wysiwyg-input
							v-model="coupon.ticket_dashboard_instructions"
							:counter="500"
							hide-details="auto"
							hint="Information the user should know before redeeming this offer"
							label="Pre-Redemption Instructions"
							outlined
							persistent-hint
							rows="4"
					></wysiwyg-input>
				</v-col>
			</v-row>

			<v-row v-if="showRedemptionValidations" class="mt-n5">
				<v-col cols="12">
					<v-switch
							v-model="expandRedemptionValidation"
							:class="expandRedemptionValidation ? 'mt-3' : 'mb-2 mt-0'"
							dense
							:disabled="!venue"
							hide-details="auto"
							inset
							label="Redemption Validation"
					></v-switch>
				</v-col>
				<template v-if="expandRedemptionValidation">
					<v-col cols="12" v-if="redeemIs('pin')">
						<template v-if="coupon.partner_id !== venue.partner_id">
							<v-alert
									class="mb-0 rounded-lg"
									border="left"
									color="warning"
									dense
									icon="mdi-alert-circle"
									text
							>
								<span class="text-body-2">
									This place is managed by another partner. You may not set
									or update this pin. You may contact the place to have a
									pin set or reach out to your Bandwango Support Specialist.
								</span>
							</v-alert>
						</template>
						<v-alert
								v-else
								class="rounded-lg mb-0"
								border="left"
								color="info"
								dense
								icon="mdi-information-slab-circle"
								text
						>
							<span class="text-body-2">
								Please Note: This same PIN number will be used
								for all redemptions with a PIN validation across
								{{ venue.name }}.
							</span>
						</v-alert>
					</v-col>

					<v-col v-if="origin!='merchant-portal'" cols="12" sm="6">
						<v-text-field
								v-model="coupon.languageTags[redeemTagIdx].value"
								class="rounded-lg required"
								clearable
								hide-details="auto"
								label="Redeem Label"
								outlined
								type="text"
								:rules="validationRules.name"
						></v-text-field>
					</v-col>
					<v-col cols="12" sm="6">
						<v-select
								v-model="coupon.redemption_validation"
								class="rounded-lg required"
								hide-details="auto"
								:items="redemptionValidations"
								label="Redeem Validation"
								outlined
						></v-select>
					</v-col>

					<v-col cols="12" v-if="redeemIs('qr')">
						<qr-code-loader
								:partner-id="venue.partner_id"
								:place-id="venue.id"
								:place-name="venue.name"
						/>
					</v-col>
					<v-col v-if="redeemIs('pin')" cols="12" sm="6">
						<bw-pin-input
								v-model="venue.pin_number"
								hide-details="auto"
								:disabled="venue.partner_id !== coupon.partner_id"
								@input="pinInput"
						/>
					</v-col>
					<v-col v-if="redeemIs('gps')" cols="12" sm="6">
						<v-text-field
								v-model.number="coupon.redemption_radius"
								v-two-decimals
								class="rounded-lg required"
								hide-details="auto"
								label="Radius"
								max="10"
								min="0.25"
								outlined
								persistent-hint
								suffix=" miles"
								:rules="validationRules.radius"
								step="0.25"
								type="number"
								@wheel.native.prevent
						></v-text-field>
					</v-col>
				</template>
			</v-row>
 			<v-row v-if="origin!='merchant-portal' && !expandRedemptionValidation" >
				<v-col>
					<v-text-field
						v-model="checkInLanguage"
						class="rounded-lg required"
						:hint="beforeHint"
						:label="beforeLabel"
						outlined
						persistent-hint
						required
						:rules="[v => !!v || 'Text is required.']"
					></v-text-field>
				</v-col>
			</v-row>

			<form-subsection title="Post Redemption Instructions">
				{{ postRedemptionTemplateDescription }}
			</form-subsection>

			<v-row>
				<v-col cols="12" sm="5" md="6" lg="5">
					<v-select
							v-model="postRedemptionInstructionsId"
							class="rounded-lg"
							clearable
							hide-details="auto"
							:items="postRedemptionInstructions"
							:label="postRedemptionTemplateLabel"
							outlined
							required
							:rules="rules.post_redemption_instructions"
							@change="postRedemptionTemplateChanged"
					></v-select>
				</v-col>
			</v-row>

			<v-row>
				<v-col>
					<wysiwyg-input
							v-model="coupon.redemption_settings.instructions"
							:counter="500"
							hide-details="auto"
							label="Post-Redemption Instructions"
							outlined
							rows="4"
							:rules="rules.redemption_instructions"
							@input="postRedemptionInstructionChanged"
					></wysiwyg-input>
				</v-col>
			</v-row>

			<v-row>
				<v-col v-if="!!afterLabel">
					<v-text-field
						v-model="checkedInLanguage"
						class="rounded-lg required"
						:hint="afterHint"
						:label="afterLabel"
						outlined
						persistent-hint
						required
						:rules="[v => !!v || 'Text is required.']"
					></v-text-field>
				</v-col>
			</v-row>
		</ui-box>
	</div>
</template>

<script>
import FormDivider from "../../../../UI/Form/FormDivider.vue";
import {helpers as sharedHelpers} from '../../../../../Shared/helpers';
import ButtonLanguage from "./ButtonLanguage.vue";
import QrCodeLoader from '../../../../Components/QrCodeLoader.vue';

export default {
	name: 'RedeemForm',
	components: { ButtonLanguage, FormDivider, QrCodeLoader },
	props: {
		coupon: {
			type: Object,
			required: true
		},
		formIsValid: {
			type: Object,
			required: true
		},
		origin: {
			type: String,
			default: ''
		},
		product: {
			type: Object,
			required: true
		},
		refs: {
			type: Object,
			required: true
		},
		rules: {
			type: Object,
			required: true
		},
		showRedemptionValidations: {
			type: Boolean,
			default: false
		},
		venue: {
			type: Object,
			required: false
		}
	},

	data() {
		return {
			instructions: [],
			howToUseDefaults: [
				{
					id: 'In-Person',
					name: 'Redeem in person. After redeeming this product, follow any additional instructions on the screen.',
				},
				{
					id: 'Online',
					name: 'Redeem online. After redeeming this product, follow any additional instructions on the screen.',
				},
				{
					id: 'Phone',
					name: 'Redeem over the phone. After redeeming this product, follow any additional instructions on the screen.',
				},
			],
			phoneIsValid: true,
			phoneRule: [
				v => !!v || 'Phone is required.',
				v => (!v || (!!v && this.phoneIsValid)) || 'Please enter a valid phone'
			],
			postRedemptionInstructionsSet: [
				{
					id: 'In-Person',
					label: 'In-Person Redeemer',
					description: 'In order to be redeemed in person, who should the pass-holder present the coupon to in the establishment?',
					options: [
						{
							value: 'in-person-0',
							text: 'Cashier',
							description: 'Present this screen to the cashier to redeem.',
						},
						{
							value: 'in-person-1',
							text: 'Server',
							description: 'Present this screen to the server upon ordering to redeem.',
						},
						{
							value: 'in-person-2',
							text: 'Bartender',
							description: 'Present this screen to the bartender upon ordering to redeem.',
						},
						{
							value: 'in-person-3',
							text: 'Attendant',
							description: 'Present this screen to the attendant upon arrival to redeem.',
						},
						{
							value: 'custom',
							text: 'Custom',
							description: '[text entry]',
						},
					]
				},
				{
					id: 'Online',
					label: 'Online Redeemer',
					description: 'Provide the website link and instructions for how the pass-holders will redeem the offer online.',
					options: [
						{
							value: 'online-0',
							text: 'Website with Promo Code',
							description: 'Click on the website link and use the promo code to redeem this offer.',
						},
						{
							value: 'online-1',
							text: 'Website Only',
							description: 'Click on the website link to redeem this offer.',
						},
						{
							value: 'custom',
							text: 'Custom',
							description: '[text entry]',
						},
					]
				},
				{
					id: 'Phone',
					label: 'Redeem over Phone Options',
					description: 'Select how pass-holders should redeem by phone, and provide any relevant instructions for how to redeem.',
					options: [
						{
							value: 'phone-0',
							text: 'Phone',
							description: 'Tap "Call To Redeem" and mention this offering to redeem.',
						},
						{
							value: 'phone-1',
							text: 'Reservation',
							description: 'Tap "Call To Redeem" and mention this offering to make your reservation',
						},
						{
							value: 'custom',
							text: 'Custom',
							description: '[text entry]',
						},
					]
				},
			],

			postRedemptionInstructionsId: null,
			redemptionLocations: [
				{
					text: 'In Person',
					value: 'In-Person',
					icon: 'mdi-account-check'
				},
				{
					text: 'Online',
					value: 'Online',
					icon: 'mdi-web-check'
				},
				{
					text: 'Phone',
					value: 'Phone',
					icon: 'mdi-cellphone-check'
				},
			],
			redemptionValidations: [
				{
						text: 'Venue Pin Number',
						value: 'pin'
				},
				{
						text: 'Device GPS',
						value: 'gps'
				},
				{
						text: 'Device GPS or Pin Number',
						value: 'gpsOrPin'
				},
				{
						text: 'QR Code Scan',
						value: 'qr'
				}
			],
			validationRules: {
				name: [v => !!v || 'Check-in label is required'],
				radius: [v => !!v || 'Check-in radius is required']
			}
		};
	},
	computed: {
		config() {
			const map = {
				'standard': {
					beforeTag: 'REDEEM',
					afterTag: null,
					beforeLabel: 'Before redemption',
					afterLabel: null,
					beforeHint: 'Use this field to customize the wording of the button that passholders use to initiate the redemption process.',
					afterHint: null,
				},
				'check-in': {
					beforeTag: 'CHECKIN',
					afterTag: 'CHECKEDIN',
					beforeLabel: 'Before check-in',
					afterLabel: 'After check-in',
					beforeHint: 'Use this field to customize the wording of the button that passholders use to initiate the check-in process.',
					afterHint: 'Text on dashboard buttons after being checked in.',
				},
				'shipped': {
					beforeTag: 'TAP_TO_SELECT',
					afterTag: null,
					beforeLabel: 'Before selection',
					afterLabel: null,
					beforeHint: 'Use this field to customize the wording of the button that passholders use.',
					afterHint: null,
				}
			};

			return map[this.coupon.redemptions] || {};
		},

		beforeTag() {
			return this.config.beforeTag;
		},
		afterTag() {
			return this.config.afterTag;
		},
		beforeLabel() {
			return this.config.beforeLabel;
		},
		afterLabel() {
			return this.config.afterLabel;
		},
		beforeHint() {
			return this.config.beforeHint;
		},
		afterHint() {
			return this.config.afterHint;
		},
		countryCode() {
			return window.userCountryCode || 'US';
		},
		enablePromoTicketField() {
			return this.coupon.tickets?.length === 1
					&& !this.coupon.tickets[0].quantity
					&& ['', 'promo_code'].includes(this.coupon.tickets[0].type)
					;
		},
		expandRedemptionValidation: {
			get() {
				return !!this.coupon.redemption_validation;
			},
			set(value) {
				if (value) {
					return this.coupon.redemption_validation = 'pin';
				}
				this.coupon.redemption_validation = '';
			}
		},
		redeemTagIdx() {
			return this.coupon.languageTags.findIndex(({ tag: t }) => t === 'REDEEM');
		},
		redemptionLocationIcon() {
			if(!this.coupon?.redemption_location)
				return undefined;
			return this.redemptionLocations.filter(
					l => l.value === this.coupon.redemption_location
			)[0].icon;
		},
		postRedemptionInstructions() {
			return this.instructions?.options;
		},
		postRedemptionTemplateDescription() {
			return this.instructions?.description;
		},
		postRedemptionTemplateLabel() {
			return this.instructions?.label;
		},
		promoTicket: {
			get() {
				const t = this.coupon.tickets?.find(x => x.type === 'promo_code' && !x.quantity);
				return t && t.value || '';
			},
			set(v) {
				const t = this.coupon.tickets.find(x => !x.quantity);
				const type = !!v ? 'promo_code' : '';
				if(t) {
					t.value = v;
					t.type = type;
				}
			}
		},
		checkInLanguage: {
			get() {
				return this.getLanguageTag(this.beforeTag).value;
			},
			set(v) {
				const {coupon: {languageTags}} = this;
				const checkinIndex = this.getLanguageTagIndex(this.beforeTag)
				languageTags[checkinIndex].value = v;
			}
		},
		checkedInLanguage: {
			get() {
				return this.getLanguageTag(this.afterTag).value;
			},
			set(v) {
				const {coupon: {languageTags}} = this;
				const checkinIndex = this.getLanguageTagIndex(this.afterTag)
				languageTags[checkinIndex].value = v;
			}
		},
	},
	mounted() {
		if(!this.coupon.tickets) {
			this.$set(this.coupon, 'tickets', [{ value: '', type: '' }]);
		}
		if(!this.coupon?.redemption_settings) {
			this.$set(this.coupon, 'redemption_settings', {})
		}
		if(!this.coupon.redemption_settings?.mode) {
			this.$set(this.coupon.redemption_settings, 'mode', 'In-Person')
		}
		if(!this.coupon.redemption_settings?.instructions) {
			this.$set(this.coupon.redemption_settings, 'instructions', '')
		}
		this.modeChanged(this.coupon.redemption_settings.mode, !this.coupon.id);
		this.postRedemptionInstructionsId = this.postRedemptionInstructions?.find(
				x => x.description === this.coupon.redemption_settings.instructions
		)?.value || 'custom';

		this.refs.redeemForm.validate();
	},
	methods: {
		modeChanged(value, updateCoupon) {
			if(value) {
				const howToUse = this.howToUseDefaults.find(
						x => x.id === value
				).name;
				const instructions = this.postRedemptionInstructionsSet.find(
						x => x.id === this.coupon.redemption_settings.mode
				);
				this.$set(this, 'instructions', instructions);

				if(updateCoupon && this.enablePromoTicketField && value !== 'Online') {
					this.promoTicket = '';
				}
				this.postRedemptionInstructionsId = instructions?.options[0]?.value;
				if(updateCoupon) {
					this.$set(this.coupon, 'ticket_dashboard_instructions', howToUse);
					this.postRedemptionTemplateChanged(this.postRedemptionInstructionsId);
				}
			}
		},
		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			if(!valid) this.formIsValid.redeem = false;
			// For field validation rule
			this.phoneIsValid = valid;
		},
		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},
		pinInput(v) {
			console.log(v);
			this.venue.pin_number = v;
		},
		postRedemptionInstructionChanged(value) {
			if(value) {
				this.postRedemptionInstructionsId = this.postRedemptionInstructions.find(
						x => x.description === sharedHelpers.getTextFromHTML(value)
				)?.value || 'custom';
			}
		},
		postRedemptionTemplateChanged(value) {
			if(value) {
				const instructions = this.postRedemptionInstructions.find(
						x => x.value === value
				)?.description;

				this.$set(this.coupon.redemption_settings, 'instructions', instructions);
			}
		},
		getLanguageTag(tag) {
			return this.coupon.languageTags?.find(
				({tag: t}) => t === tag
			) || {value: ''};
		},
		getLanguageTagIndex(tag) {
			const {coupon: {languageTags}} = this;
			return languageTags.findIndex(
				({tag: t}) => t === tag
			);
		},
		redeemIs(type) {
			return this.coupon.redemption_validation.toLowerCase().includes(type);
		}
	}
}
</script>

<style scoped>
/* Add your styles here */
</style>